import { memo, useMemo } from "react";
import { Helmet } from "react-helmet";
import { isDesktop } from "react-device-detect";
import { useNavigate, useSearchParams } from "react-router-dom";
import MarkitHeaderLogo from "../../components/MarkitHeaderLogo";
import RectangleButton from "../../components/Buttons/RectangleButton";
import { NavigationId } from "../../navigation/AppParamList";
import { Colors } from "../../utils/colors";
import { MARKITAI } from "@markit/common.utils";
import { MAX_INSTAGRAM_ACCOUNTS } from "../../components/CreatorDashboard/AutoDM/Instagram/InstagramPanelSection";
import { SocialPlatformIconItem } from "../../components/DisplayItem/AutoDM/SocialPlatformIconItem";
import { DMAutomationPlatform } from "@markit/common.types";

const InstagramConnectionPage = memo(function InstagramConnectionPageFn() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isFailure = searchParams.get("ig") === "failure";
  const isAccountLimit = searchParams.get("ig") === "account_limit";
  const isDeletion = searchParams.get("ig") === "deletion";
  const isSuccess = !isFailure && !isAccountLimit && !isDeletion;
  const igUsername =
    searchParams.get("username")?.trim().replace(/^@/, "") ?? "";

  const successHeadline = igUsername
    ? `${igUsername} is connected!`
    : "Instagram is connected!";

  const content = useMemo(
    () =>
      isAccountLimit
        ? {
            header: "Something Failed!",
            title: "Account Limit Reached",
            subtitle: `You can connect up to ${MAX_INSTAGRAM_ACCOUNTS} Instagram accounts. Please disconnect one before adding another.`,
            buttonLabel: "Back to Markit",
          }
        : isFailure
          ? {
              header: "Something Failed!",
              title: "Instagram Connection Failed",
              subtitle:
                "We could not connect your Instagram account. Please try again from your integrations page.",
              buttonLabel: "Back to Markit",
            }
          : isDeletion
            ? {
                header: "Updated",
                title: "Instagram Disconnected",
                subtitle:
                  "We've removed your Instagram link from Markit and deleted the access we had to your account. Automations that used this channel will no longer run until you connect again.",
                buttonLabel: "Back to Markit",
              }
            : {
                header: "Success!",
                title: successHeadline,
                subtitle:
                  "Create your first Instagram automation and try the power of the Instagram channel.",
                buttonLabel: "Let's Start",
              },
    [isAccountLimit, isDeletion, isFailure, successHeadline]
  );

  return (
    <div
      style={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Helmet>
        <title>Instagram Connection | Markit AI</title>
        <meta
          name="description"
          content={
            isFailure
              ? "Your Instagram account could not be connected."
              : isDeletion
                ? "Your Instagram account has been disconnected."
                : "Your Instagram account has been successfully connected."
          }
        />
        <link rel="canonical" href={MARKITAI + "/instagram/connection"} />
      </Helmet>
      <div style={{ position: "absolute", top: 24, left: 24 }}>
        <MarkitHeaderLogo fullLogo alternateSize={24} />
      </div>
      <div
        className="ColumnCenter"
        style={{
          minHeight: "100vh",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          className={isDesktop ? "AlignedRow" : "ColumnNormal"}
          style={{
            width: "100%",
            maxWidth: isDesktop ? 920 : 480,
            gap: isDesktop ? 80 : 14,
            alignItems: isDesktop ? "center" : "flex-start",
            justifyContent: "center",
          }}
        >
          {isDesktop ? null : (
            <div style={{ paddingInline: 14 }}>
              <SocialPlatformIconItem
                platform={DMAutomationPlatform.INSTAGRAM}
                size={40}
                noBorder
              />
            </div>
          )}
          <div
            className="ColumnNormal"
            style={{
              maxWidth: 480,
              gap: isDesktop ? 24 : 14,
              paddingInline: isDesktop ? 0 : 14,
            }}
          >
            <div className="ColumnNormal" style={{ gap: 14 }}>
              <span
                className="bodyMedium"
                style={{
                  color:
                    isFailure || isAccountLimit ? Colors.RED3 : Colors.BLUE5,
                }}
              >
                {content.header}
              </span>
              <span
                style={{
                  fontSize: 34,
                  fontWeight: 500,
                  display: "block",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                {content.title}
              </span>
              <span className="bodySubtext" style={{ maxWidth: 400 }}>
                {content.subtitle}
              </span>
            </div>
            {!isDesktop && isSuccess ? (
              <span className="bodySubtext">You can close this window.</span>
            ) : (
              <RectangleButton
                buttonLabel={content.buttonLabel}
                onPress={() => navigate(NavigationId.HOME_AUTO_DM)}
                altColor={Colors.BLACK}
                altTextColor={Colors.WHITE}
                altPaddingHorz={40}
                altPaddingVert={14}
                containerStyles={{ width: "fit-content" }}
              />
            )}
          </div>
          {isDesktop ? (
            <SocialPlatformIconItem
              platform={DMAutomationPlatform.INSTAGRAM}
              size={150}
              noBorder
            />
          ) : null}
        </div>
      </div>
    </div>
  );
});

export default InstagramConnectionPage;
